import { useEffect } from "react";
import { Link } from "react-router-dom";

export type LegalBlock =
  | { type: "title" | "heading" | "subheading" | "paragraph"; text: string }
  | { type: "list"; items: string[] };

type LegalDocumentProps = {
  content: LegalBlock[];
  documentTitle: string;
  kicker: string;
};

export default function LegalDocument({ content, documentTitle, kicker }: LegalDocumentProps) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${documentTitle} | Vay3Phút`;
    return () => {
      document.title = previousTitle;
    };
  }, [documentTitle]);

  return (
    <main className="min-h-screen bg-[#fff9f3] text-[#111a3f]">
      <header className="border-b border-[#e7e6eb] bg-white/95 px-4 py-4 backdrop-blur md:px-8">
        <div className="mx-auto max-w-5xl">
          <Link to="/" aria-label="Vay3Phút - Trang chủ">
            <img
              src="/vay3phut/vay3phut-logo.png"
              alt="Vay3Phút"
              className="h-auto w-[150px] md:w-[178px]"
            />
          </Link>
        </div>
      </header>

      <section className="px-4 py-10 md:px-8 md:py-16">
        <article className="mx-auto max-w-4xl rounded-[22px] border border-[#e7e6eb] bg-white px-6 py-8 shadow-[0_24px_70px_rgba(17,26,63,0.08)] md:px-12 md:py-12">
          <p className="mb-4 text-xs font-extrabold uppercase tracking-[0.14em] text-[#ff5d41]">
            {kicker}
          </p>
          <div className="space-y-4">
            {content.map((block, index) => {
              if (block.type === "title") {
                return (
                  <h1
                    key={index}
                    className="mb-10 font-['Manrope'] text-3xl font-extrabold leading-tight tracking-[-0.035em] text-[#111a3f] md:text-4xl"
                  >
                    {block.text}
                  </h1>
                );
              }

              if (block.type === "heading") {
                return (
                  <h2
                    key={index}
                    className="mt-10 border-b border-[#e7e6eb] pb-3 font-['Manrope'] text-xl font-extrabold leading-snug text-[#101747] md:text-2xl"
                  >
                    {block.text}
                  </h2>
                );
              }

              if (block.type === "subheading") {
                return (
                  <h3
                    key={index}
                    className="mt-7 font-['Manrope'] text-base font-extrabold leading-relaxed text-[#ff5d41] md:text-lg"
                  >
                    {block.text}
                  </h3>
                );
              }

              if (block.type === "list") {
                return (
                  <ul
                    key={index}
                    className="ml-5 list-disc space-y-2 text-sm leading-7 text-[#5d6278] marker:text-[#ff5d41] md:text-[15px]"
                  >
                    {block.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="pl-1">
                        {item}
                      </li>
                    ))}
                  </ul>
                );
              }

              return (
                <p
                  key={index}
                  className="whitespace-pre-line text-sm leading-7 text-[#5d6278] md:text-[15px]"
                >
                  {block.text}
                </p>
              );
            })}
          </div>
        </article>
      </section>
    </main>
  );
}
